import React, { useState, useEffect, useRef } from "react";
import {
  Modal,
  Box,
  Typography,
  Button,
  Chip,
  Divider,
  Stack,
  Alert,
  CircularProgress,
} from "@mui/material";
import socketService from "../socket/newsocketService";
import apiClient from "../Utils/apiClient";
import soundFileSiren from "../Raw/Siren.wav";

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 580,
  maxWidth: "95vw",
  bgcolor: "#1a1d24",
  color: "#ffffff",
  boxShadow: "0 24px 60px rgba(220, 38, 38, 0.4)",
  borderRadius: 4,
  p: 3.5,
  outline: "none",
  border: "2px solid #ef4444",
};

export default function HeartbeatThreatModal() {
  const [isOpen, setIsOpen] = useState(false);
  const [threatData, setThreatData] = useState(null);
  const [sendingAlert, setSendingAlert] = useState(false);
  const [alertSentStatus, setAlertSentStatus] = useState(null);
  const audioRef = useRef(null);

  useEffect(() => {
    // Listen for Dead Man's Switch Threat Alerts via WebSockets
    const handleThreatAlert = (data) => {
      console.log("🚨 [HeartbeatThreatModal] Received Threat Alert:", data);
      setThreatData(data);
      setIsOpen(true);
      setAlertSentStatus(null);

      // Play Siren audio
      try {
        if (!audioRef.current) {
          audioRef.current = new Audio(soundFileSiren);
        }
        audioRef.current.loop = true;
        audioRef.current.play().catch((e) => console.log("Audio autoplay prevented:", e));
      } catch (_) {}
    };

    const handleBatteryShutdown = (data) => {
      console.log("ℹ️ [HeartbeatThreatModal] Received Normal Shutdown:", data);
    };

    socketService.on("escort_threat_alert", handleThreatAlert);
    socketService.on("escort_battery_shutdown", handleBatteryShutdown);

    return () => {
      socketService.off("escort_threat_alert", handleThreatAlert);
      socketService.off("escort_battery_shutdown", handleBatteryShutdown);
      stopAudio();
    };
  }, []);

  const stopAudio = () => {
    if (audioRef.current) {
      try {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      } catch (_) {}
    }
  };

  const handleClose = () => {
    stopAudio();
    setIsOpen(false);
  };

  const handleSendToEmergencyContacts = async () => {
    if (!threatData?.user_id) return;
    setSendingAlert(true);
    setAlertSentStatus(null);

    try {
      const response = await apiClient.post("/heartbeat/notify-emergency", {
        user_id: threatData.user_id,
        trip_id: threatData.trip_id,
        latitude: threatData.latitude,
        longitude: threatData.longitude,
        battery_level: threatData.battery_level,
      });

      if (response?.data?.success || response?.status === 200 || response?.success) {
        setAlertSentStatus({
          type: "success",
          msg: "Emergency contacts notified successfully with last-known coordinates!",
        });
      } else {
        setAlertSentStatus({
          type: "warning",
          msg: response?.data?.message || "Notification queued for emergency contacts.",
        });
      }
    } catch (err) {
      setAlertSentStatus({
        type: "error",
        msg: err?.response?.data?.message || err?.message || "Failed to notify emergency contacts.",
      });
    } finally {
      setSendingAlert(false);
    }
  };

  if (!threatData) return null;

  const mapsUrl =
    threatData.latitude && threatData.longitude
      ? `https://maps.google.com/?q=${threatData.latitude},${threatData.longitude}`
      : null;

  return (
    <Modal
      open={isOpen}
      onClose={handleClose}
      aria-labelledby="threat-modal-title"
      aria-describedby="threat-modal-description"
    >
      <Box sx={modalStyle}>
        {/* Header Badge */}
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.5}>
          <Chip
            label="CRITICAL THREAT FLAG"
            color="error"
            sx={{ fontWeight: "bold", fontSize: "0.85rem", letterSpacing: 1 }}
          />
          <Button
            size="small"
            onClick={handleClose}
            sx={{ color: "#9ca3af", minWidth: 32, fontSize: 16 }}
          >
            ✕
          </Button>
        </Stack>

        <Typography id="threat-modal-title" variant="h5" component="h2" sx={{ color: "#f87171", fontWeight: 700, mb: 1 }}>
          🚨 Dead Man's Switch Triggered!
        </Typography>

        <Typography variant="body2" sx={{ color: "#d1d5db", mb: 2 }}>
          User communication halted abruptly without warning. The 10-minute heartbeat buffer clock has expired.
        </Typography>

        <Divider sx={{ borderColor: "#374151", mb: 2 }} />

        {/* Telemetry Display */}
        <Box sx={{ bgcolor: "#111827", p: 2, borderRadius: 2, mb: 2 }}>
          <Stack spacing={1}>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="caption" sx={{ color: "#9ca3af" }}>TRIP ID:</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, color: "#f3f4f6" }}>
                {threatData.trip_id || "N/A"}
              </Typography>
            </Stack>

            <Stack direction="row" justifyContent="space-between">
              <Typography variant="caption" sx={{ color: "#9ca3af" }}>USER ID:</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, color: "#f3f4f6" }}>
                {threatData.user_id || "N/A"}
              </Typography>
            </Stack>

            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="caption" sx={{ color: "#9ca3af" }}>LAST BATTERY LEVEL:</Typography>
              <Chip
                label={`${threatData.battery_level ?? "N/A"}% (High)`}
                size="small"
                sx={{
                  bgcolor: "#7f1d1d",
                  color: "#fecaca",
                  fontWeight: "bold",
                  fontSize: "0.75rem",
                }}
              />
            </Stack>

            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="caption" sx={{ color: "#9ca3af" }}>GPS COORDINATES:</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, color: "#60a5fa" }}>
                {threatData.latitude ? `${threatData.latitude.toFixed(5)}, ${threatData.longitude.toFixed(5)}` : "Unavailable"}
              </Typography>
            </Stack>

            <Stack direction="row" justifyContent="space-between">
              <Typography variant="caption" sx={{ color: "#9ca3af" }}>MINUTES OFFLINE:</Typography>
              <Typography variant="body2" sx={{ fontWeight: 700, color: "#ef4444" }}>
                {threatData.minutes_offline || "> 10"} Minutes
              </Typography>
            </Stack>
          </Stack>
        </Box>

        {/* Battery Proof Notice */}
        <Alert severity="error" sx={{ bgcolor: "rgba(239, 68, 68, 0.15)", color: "#fca5a5", mb: 2.5, "& .MuiAlert-icon": { color: "#f87171" } }}>
          Battery was healthy ({threatData.battery_level}%). Device did NOT shut down normally. Possible device tampering, seizure, or forced blackout!
        </Alert>

        {alertSentStatus && (
          <Alert severity={alertSentStatus.type} sx={{ mb: 2 }}>
            {alertSentStatus.msg}
          </Alert>
        )}

        {/* Action Buttons */}
        <Stack direction="row" spacing={1.5} justifyContent="flex-end">
          {mapsUrl && (
            <Button
              variant="outlined"
              color="info"
              component="a"
              href={mapsUrl}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ borderColor: "#3b82f6", color: "#60a5fa" }}
            >
              📍 View on Map
            </Button>
          )}

          <Button
            variant="contained"
            color="error"
            onClick={handleSendToEmergencyContacts}
            disabled={sendingAlert}
            startIcon={sendingAlert ? <CircularProgress size={16} color="inherit" /> : null}
            sx={{
              bgcolor: "#dc2626",
              "&:hover": { bgcolor: "#b91c1c" },
              fontWeight: 700,
            }}
          >
            {sendingAlert ? "Dispatching..." : "Send to Emergency Contacts"}
          </Button>

          <Button
            variant="contained"
            onClick={handleClose}
            sx={{ bgcolor: "#374151", color: "#f3f4f6", "&:hover": { bgcolor: "#4b5563" } }}
          >
            Acknowledge
          </Button>
        </Stack>
      </Box>
    </Modal>
  );
}
